import { lazy } from 'react';

// project imports
import WarehouseLayout from 'layout/WarehouseLayout';
import Loadable from 'ui-component/Loadable';
import {
  MANAGED_INVENTORY_ROUTES,
  OLD_MANAGED_INVENTORY_ROUTES,
} from 'routes/constants';
import PermissionsRouteWrapper from 'views/pages/components/PermissionsRouteWrapper';
import { ALL_APPS } from 'constants/appConstants';
import AuthGuard from 'utils/route-guard/AuthGuard';
import OrgGuard from 'utils/route-guard/OrgGuard';
import { Redirect } from 'react-router6-redirect';
import { WarehouseWorkstationProvider } from 'contexts/WarehouseWorkstationContext';
import { ReceiveV2Provider } from 'views/warehouse/ReceiveV2/ReceiveV2Context';
import { ScannerProvider } from 'contexts/ScannerContext';
import { FixedPickingProvider } from 'views/warehouse/FixedPicking/FixedPickingContext';
import { WarehouseKitRequestProvider } from 'contexts/WarehouseKitRequestContext';
import SerialProvider from 'contexts/SerialProvider';
import { CountingProvider } from 'views/warehouse/Counting/CountingContext';
import { HandlingRequestsProvider } from 'views/warehouse/Kitting/HandlingRequestsContext';
import { MobilePickingProvider } from 'views/warehouse/MobilePicking/MobilePickingContext';
import { MoveWarningProvider } from 'views/warehouse/MoveWarningContext';
import { OrgAndTeamProvider } from 'views/warehouse/OrgAndTeamContext';
import { DashboardProvider } from 'views/warehouse/dashboard/DashboardContext';

const Dashboard = Loadable(lazy(() => import('views/warehouse/dashboard')));
const Transfer = Loadable(lazy(() => import('views/warehouse/transfer')));
const Counting = Loadable(lazy(() => import('views/warehouse/Counting')));
const Settings = Loadable(lazy(() => import('views/warehouse/settings')));
const Kitting = Loadable(lazy(() => import('views/warehouse/Kitting')));
const Receive = Loadable(lazy(() => import('views/warehouse/receive')));
const Shipping = Loadable(lazy(() => import('views/warehouse/Shipping')));
const StandardLayout = Loadable(
  lazy(() => import('views/warehouse/StandardLayout'))
);
const InventoryStockLot = Loadable(
  lazy(() => import('views/warehouse/stockLot'))
);
const InventoryStockLocation = Loadable(
  lazy(() => import('views/warehouse/stockLocation'))
);
const PreReceive = Loadable(
  lazy(() => import('views/warehouse/ReceiveV2/PreReceive'))
);
const ManualReceive = Loadable(
  lazy(() => import('views/warehouse/ReceiveV2/ManualReceive'))
);
const FixedReceive = Loadable(
  lazy(() => import('views/warehouse/ReceiveV2/FixedReceive'))
);
const InlineReceive = Loadable(
  lazy(() => import('views/warehouse/ReceiveV2/InlineReceive'))
);
const FixedPicking = Loadable(
  lazy(() => import('views/warehouse/FixedPicking'))
);
const MobilePicking = Loadable(
  lazy(() => import('views/warehouse/MobilePicking'))
);
const KitDashboard = Loadable(
  lazy(() => import('views/warehouse/KitDashboard'))
);

// sample page routing

// ==============================|| MAIN ROUTING ||============================== //

const UserAccountRoutes = {
  path: '/',
  element: (
    <AuthGuard>
      <OrgGuard>
        <OrgAndTeamProvider>
          <WarehouseLayout />
        </OrgAndTeamProvider>
      </OrgGuard>
    </AuthGuard>
  ),
  children: [
    // Redirect routes for old mobile inventory paths
    {
      path: OLD_MANAGED_INVENTORY_ROUTES.DASHBOARD,
      element: <Redirect to={MANAGED_INVENTORY_ROUTES.DASHBOARD} />,
    },
    {
      path: OLD_MANAGED_INVENTORY_ROUTES.TRANSFER,
      element: <Redirect to={MANAGED_INVENTORY_ROUTES.TRANSFER} />,
    },
    {
      path: OLD_MANAGED_INVENTORY_ROUTES.COUNTER,
      element: <Redirect to={MANAGED_INVENTORY_ROUTES.COUNTER} />,
    },
    {
      path: OLD_MANAGED_INVENTORY_ROUTES.SETTINGS,
      element: <Redirect to={MANAGED_INVENTORY_ROUTES.SETTINGS} />,
    },
    {
      path: OLD_MANAGED_INVENTORY_ROUTES.PICKING,
      element: <Redirect to={MANAGED_INVENTORY_ROUTES.PICKING} />,
    },
    {
      path: OLD_MANAGED_INVENTORY_ROUTES.HANDLING,
      element: <Redirect to={MANAGED_INVENTORY_ROUTES.KITTING} />,
    },
    {
      path: OLD_MANAGED_INVENTORY_ROUTES.KITTING,
      element: <Redirect to={MANAGED_INVENTORY_ROUTES.KITTING} />,
    },
    {
      path: OLD_MANAGED_INVENTORY_ROUTES.RECEIVE,
      element: <Redirect to={MANAGED_INVENTORY_ROUTES.RECEIVE} />,
    },
    {
      path: OLD_MANAGED_INVENTORY_ROUTES.SHIP,
      element: <Redirect to={MANAGED_INVENTORY_ROUTES.SHIPPING} />,
    },
    {
      path: OLD_MANAGED_INVENTORY_ROUTES.SHIPPING,
      element: <Redirect to={MANAGED_INVENTORY_ROUTES.SHIPPING} />,
    },
    {
      path: OLD_MANAGED_INVENTORY_ROUTES.STOCK_LOTS,
      element: <Redirect to={MANAGED_INVENTORY_ROUTES.STOCK_LOTS} />,
    },
    {
      path: OLD_MANAGED_INVENTORY_ROUTES.STOCK_LOCATIONS,
      element: <Redirect to={MANAGED_INVENTORY_ROUTES.STOCK_LOCATIONS} />,
    },
    {
      path: OLD_MANAGED_INVENTORY_ROUTES.PRE_RECEIVE,
      element: <Redirect to={MANAGED_INVENTORY_ROUTES.PRE_RECEIVE} />,
    },
    {
      path: OLD_MANAGED_INVENTORY_ROUTES.MANUAL_RECEIVE,
      element: <Redirect to={MANAGED_INVENTORY_ROUTES.MANUAL_RECEIVE} />,
    },
    {
      path: OLD_MANAGED_INVENTORY_ROUTES.FIXED_RECEIVE,
      element: <Redirect to={MANAGED_INVENTORY_ROUTES.FIXED_RECEIVE} />,
    },
    {
      path: OLD_MANAGED_INVENTORY_ROUTES.INLINE_RECEIVE,
      element: <Redirect to={MANAGED_INVENTORY_ROUTES.INLINE_RECEIVE} />,
    },
    {
      path: OLD_MANAGED_INVENTORY_ROUTES.FIXED_PICKING,
      element: <Redirect to={MANAGED_INVENTORY_ROUTES.FIXED_PICKING} />,
    },
    {
      path: OLD_MANAGED_INVENTORY_ROUTES.MOBILE_PICKING,
      element: <Redirect to={MANAGED_INVENTORY_ROUTES.MOBILE_PICKING} />,
    },
    {
      path: OLD_MANAGED_INVENTORY_ROUTES.KIT_DASHBOARD,
      element: <Redirect to={MANAGED_INVENTORY_ROUTES.KIT_DASHBOARD} />,
    },
    {
      path: MANAGED_INVENTORY_ROUTES.DASHBOARD,
      element: (
        <PermissionsRouteWrapper
          permissionScope={{ app: ALL_APPS.WAREHOUSE.id }}
        >
          <WarehouseKitRequestProvider>
            <ScannerProvider>
              <MoveWarningProvider>
                <DashboardProvider>
                  <Dashboard />
                </DashboardProvider>
              </MoveWarningProvider>
            </ScannerProvider>
          </WarehouseKitRequestProvider>
        </PermissionsRouteWrapper>
      ),
    },
    {
      path: MANAGED_INVENTORY_ROUTES.TRANSFER,
      element: (
        <PermissionsRouteWrapper
          permissionScope={{ app: ALL_APPS.WAREHOUSE.id }}
        >
          <WarehouseKitRequestProvider>
            <ScannerProvider>
              <MoveWarningProvider>
                <Transfer />
              </MoveWarningProvider>
            </ScannerProvider>
          </WarehouseKitRequestProvider>
        </PermissionsRouteWrapper>
      ),
    },
    {
      path: MANAGED_INVENTORY_ROUTES.COUNTER,
      element: (
        <PermissionsRouteWrapper
          permissionScope={{ app: ALL_APPS.WAREHOUSE.id }}
        >
          <WarehouseKitRequestProvider>
            <ScannerProvider>
              <MoveWarningProvider>
                <SerialProvider>
                  <CountingProvider>
                    <Counting />
                  </CountingProvider>
                </SerialProvider>
              </MoveWarningProvider>
            </ScannerProvider>
          </WarehouseKitRequestProvider>
        </PermissionsRouteWrapper>
      ),
    },
    {
      path: MANAGED_INVENTORY_ROUTES.SETTINGS,
      element: (
        <PermissionsRouteWrapper
          permissionScope={{ app: ALL_APPS.WAREHOUSE.id }}
        >
          <WarehouseKitRequestProvider>
            <Settings />
          </WarehouseKitRequestProvider>
        </PermissionsRouteWrapper>
      ),
    },
    {
      path: MANAGED_INVENTORY_ROUTES.PICKING,
      element: (
        <PermissionsRouteWrapper
          permissionScope={{ app: ALL_APPS.WAREHOUSE.id }}
        >
          <WarehouseKitRequestProvider>
            <ScannerProvider>
              <WarehouseWorkstationProvider>
                <MoveWarningProvider>
                  <MobilePickingProvider>
                    <MobilePicking />
                  </MobilePickingProvider>
                </MoveWarningProvider>
              </WarehouseWorkstationProvider>
            </ScannerProvider>
          </WarehouseKitRequestProvider>
        </PermissionsRouteWrapper>
      ),
    },
    {
      path: MANAGED_INVENTORY_ROUTES.HANDLING,
      element: <Redirect to={MANAGED_INVENTORY_ROUTES.KITTING} />,
    },
    {
      path: MANAGED_INVENTORY_ROUTES.KITTING,
      element: (
        <PermissionsRouteWrapper
          permissionScope={{ app: ALL_APPS.WAREHOUSE.id }}
        >
          <WarehouseKitRequestProvider>
            <ScannerProvider>
              <MoveWarningProvider>
                <WarehouseWorkstationProvider>
                  <HandlingRequestsProvider>
                    <Kitting />
                  </HandlingRequestsProvider>
                </WarehouseWorkstationProvider>
              </MoveWarningProvider>
            </ScannerProvider>
          </WarehouseKitRequestProvider>
        </PermissionsRouteWrapper>
      ),
    },
    {
      path: MANAGED_INVENTORY_ROUTES.RECEIVE,
      element: (
        <PermissionsRouteWrapper
          permissionScope={{ app: ALL_APPS.WAREHOUSE.id }}
        >
          <WarehouseKitRequestProvider>
            <ScannerProvider>
              <MoveWarningProvider>
                <Receive />
              </MoveWarningProvider>
            </ScannerProvider>
          </WarehouseKitRequestProvider>
        </PermissionsRouteWrapper>
      ),
    },
    {
      path: MANAGED_INVENTORY_ROUTES.SHIP,
      element: <Redirect to={MANAGED_INVENTORY_ROUTES.SHIPPING} />,
    },
    {
      path: MANAGED_INVENTORY_ROUTES.SHIPPING,
      element: (
        <PermissionsRouteWrapper
          permissionScope={{ app: ALL_APPS.WAREHOUSE.id }}
        >
          <WarehouseKitRequestProvider>
            <ScannerProvider>
              <MoveWarningProvider>
                <Shipping />
              </MoveWarningProvider>
            </ScannerProvider>
          </WarehouseKitRequestProvider>
        </PermissionsRouteWrapper>
      ),
    },
    {
      path: MANAGED_INVENTORY_ROUTES.STOCK_LOTS,
      element: (
        <PermissionsRouteWrapper
          permissionScope={{ app: ALL_APPS.WAREHOUSE.id }}
        >
          <WarehouseKitRequestProvider>
            <ScannerProvider>
              <MoveWarningProvider>
                <StandardLayout />
              </MoveWarningProvider>
            </ScannerProvider>
          </WarehouseKitRequestProvider>
        </PermissionsRouteWrapper>
      ),
      children: [
        {
          path: `${MANAGED_INVENTORY_ROUTES.STOCK_LOTS}/:stockLotId`,
          element: (
            <PermissionsRouteWrapper
              permissionScope={{ app: ALL_APPS.WAREHOUSE.id }}
            >
              <WarehouseKitRequestProvider>
                <ScannerProvider>
                  <MoveWarningProvider>
                    <InventoryStockLot />
                  </MoveWarningProvider>
                </ScannerProvider>
              </WarehouseKitRequestProvider>
            </PermissionsRouteWrapper>
          ),
        },
      ],
    },
    {
      path: MANAGED_INVENTORY_ROUTES.STOCK_LOCATIONS,
      element: (
        <PermissionsRouteWrapper
          permissionScope={{ app: ALL_APPS.WAREHOUSE.id }}
        >
          <WarehouseKitRequestProvider>
            <ScannerProvider>
              <MoveWarningProvider>
                <StandardLayout />
              </MoveWarningProvider>
            </ScannerProvider>
          </WarehouseKitRequestProvider>
        </PermissionsRouteWrapper>
      ),
      children: [
        {
          path: `${MANAGED_INVENTORY_ROUTES.STOCK_LOCATIONS}/:stockLocationId`,
          element: (
            <PermissionsRouteWrapper
              permissionScope={{ app: ALL_APPS.WAREHOUSE.id }}
            >
              <WarehouseKitRequestProvider>
                <ScannerProvider>
                  <MoveWarningProvider>
                    <InventoryStockLocation />
                  </MoveWarningProvider>
                </ScannerProvider>
              </WarehouseKitRequestProvider>
            </PermissionsRouteWrapper>
          ),
        },
      ],
    },
    {
      path: MANAGED_INVENTORY_ROUTES.PRE_RECEIVE,
      element: (
        <PermissionsRouteWrapper
          permissionScope={{ app: ALL_APPS.WAREHOUSE.id }}
        >
          <WarehouseKitRequestProvider>
            <ScannerProvider>
              <MoveWarningProvider>
                <WarehouseWorkstationProvider>
                  <ReceiveV2Provider>
                    <PreReceive />
                  </ReceiveV2Provider>
                </WarehouseWorkstationProvider>
              </MoveWarningProvider>
            </ScannerProvider>
          </WarehouseKitRequestProvider>
        </PermissionsRouteWrapper>
      ),
    },
    {
      path: MANAGED_INVENTORY_ROUTES.MANUAL_RECEIVE,
      element: (
        <PermissionsRouteWrapper
          permissionScope={{ app: ALL_APPS.WAREHOUSE.id }}
        >
          <WarehouseKitRequestProvider>
            <ScannerProvider>
              <MoveWarningProvider>
                <WarehouseWorkstationProvider>
                  <ReceiveV2Provider>
                    <ManualReceive />
                  </ReceiveV2Provider>
                </WarehouseWorkstationProvider>
              </MoveWarningProvider>
            </ScannerProvider>
          </WarehouseKitRequestProvider>
        </PermissionsRouteWrapper>
      ),
    },
    {
      path: MANAGED_INVENTORY_ROUTES.FIXED_RECEIVE,
      element: (
        <PermissionsRouteWrapper
          permissionScope={{ app: ALL_APPS.WAREHOUSE.id }}
        >
          <WarehouseKitRequestProvider>
            <ScannerProvider>
              <MoveWarningProvider>
                <WarehouseWorkstationProvider>
                  <ReceiveV2Provider>
                    <FixedReceive />
                  </ReceiveV2Provider>
                </WarehouseWorkstationProvider>
              </MoveWarningProvider>
            </ScannerProvider>
          </WarehouseKitRequestProvider>
        </PermissionsRouteWrapper>
      ),
    },
    {
      path: MANAGED_INVENTORY_ROUTES.INLINE_RECEIVE,
      element: (
        <PermissionsRouteWrapper
          permissionScope={{ app: ALL_APPS.WAREHOUSE.id }}
        >
          <WarehouseKitRequestProvider>
            <ScannerProvider>
              <MoveWarningProvider>
                <WarehouseWorkstationProvider>
                  <InlineReceive />
                </WarehouseWorkstationProvider>
              </MoveWarningProvider>
            </ScannerProvider>
          </WarehouseKitRequestProvider>
        </PermissionsRouteWrapper>
      ),
    },
    {
      path: MANAGED_INVENTORY_ROUTES.FIXED_PICKING,
      element: (
        <PermissionsRouteWrapper
          permissionScope={{ app: ALL_APPS.WAREHOUSE.id }}
        >
          <WarehouseKitRequestProvider>
            <ScannerProvider>
              <MoveWarningProvider>
                <WarehouseWorkstationProvider>
                  <FixedPickingProvider>
                    <FixedPicking />
                  </FixedPickingProvider>
                </WarehouseWorkstationProvider>
              </MoveWarningProvider>
            </ScannerProvider>
          </WarehouseKitRequestProvider>
        </PermissionsRouteWrapper>
      ),
    },
    {
      path: MANAGED_INVENTORY_ROUTES.MOBILE_PICKING,
      element: (
        <PermissionsRouteWrapper
          permissionScope={{ app: ALL_APPS.WAREHOUSE.id }}
        >
          <WarehouseKitRequestProvider>
            <ScannerProvider>
              <WarehouseWorkstationProvider>
                <MoveWarningProvider>
                  <MobilePickingProvider>
                    <MobilePicking />
                  </MobilePickingProvider>
                </MoveWarningProvider>
              </WarehouseWorkstationProvider>
            </ScannerProvider>
          </WarehouseKitRequestProvider>
        </PermissionsRouteWrapper>
      ),
    },
    {
      path: MANAGED_INVENTORY_ROUTES.KIT_DASHBOARD,
      element: (
        <PermissionsRouteWrapper
          permissionScope={{ app: ALL_APPS.WAREHOUSE.id }}
        >
          <WarehouseKitRequestProvider>
            <ScannerProvider>
              <MoveWarningProvider>
                <KitDashboard />
              </MoveWarningProvider>
            </ScannerProvider>
          </WarehouseKitRequestProvider>
        </PermissionsRouteWrapper>
      ),
    },
  ],
};

export default UserAccountRoutes;
